import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ActionBar, AddIcon, Dialog, Label, Text, UsersIcon } from '@viapeople/components'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { type Column, GenericTable } from '../../components/GenericTable'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'
import type { User, UserSearchInput } from './types'

export function UsersPage() {
	const navigate = useNavigate()
	const toast = useToast()
	const queryClient = useQueryClient()
	const [selectedUsers, setSelectedUsers] = useState<User[]>([])
	const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false)
	const { data: populationLock } = useQuery({
		...trpc.client.users.admin.getPopulationLockStatus.queryOptions(),
	})
	const isPopulationLockKnown = populationLock !== undefined
	const isPopulationLocked = populationLock?.locked === true
	const canMutatePopulation = isPopulationLockKnown && !isPopulationLocked

	const { data: customFieldsData = [] } = useQuery({
		...trpc.client.users.admin.getCustomFields.queryOptions(),
	})
	const customFields = customFieldsData as string[]

	const deleteUsersMutation = useMutation({
		...trpc.client.users.admin.delete.mutationOptions(),
		onSuccess: () => {
			toast.showToast('Users archived')
			setSelectedUsers([])
		},
		onError: (error) => {
			toast.showToast(`Error archiving users: ${error.message}`, 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.client.users.admin.search.queryKey({}),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const changeStatusMutation = useMutation({
		...trpc.client.users.admin.changeStatus.mutationOptions(),
		onSuccess: () => {
			toast.showToast('User status updated')
			setSelectedUsers([])
		},
		onError: (error) => {
			toast.showToast(`Error updating user status: ${error.message}`, 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.client.users.admin.search.queryKey({}),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const handleArchiveSelectedUsers = () => {
		if (!canMutatePopulation) {
			toast.showToast(populationLock?.reason ?? 'Population editing is locked by Data Feed.', 'error')
			return
		}

		const userIds = selectedUsers.map((u) => u.id)
		deleteUsersMutation.mutate({ userIds })
		setIsArchiveModalOpen(false)
	}

	const handleActivateSelectedUsers = () => {
		if (!canMutatePopulation) {
			toast.showToast(populationLock?.reason ?? 'Population editing is locked by Data Feed.', 'error')
			return
		}

		const userIds = selectedUsers.filter((u) => !u.active).map((u) => u.id)
		if (userIds.length > 0) {
			changeStatusMutation.mutate({ userIds, active: true })
		}
	}

	const handleDeactivateSelectedUsers = () => {
		if (!canMutatePopulation) {
			toast.showToast(populationLock?.reason ?? 'Population editing is locked by Data Feed.', 'error')
			return
		}

		const userIds = selectedUsers.filter((u) => u.active).map((u) => u.id)
		if (userIds.length > 0) {
			changeStatusMutation.mutate({ userIds, active: false })
		}
	}

	const queryOptionsGenerator = (options: UserSearchInput) => ({
		...trpc.client.users.admin.search.queryOptions(options),
	})

	const columns: Column<User>[] = [
		{
			field: 'firstName',
			label: 'Name',
			colSpan: 2,
			minWidth: '180px',
			render: (user: User) => `${user.firstName} ${user.lastName}`,
		},
		{
			field: 'externalId',
			label: 'ID',
			colSpan: 1,
			minWidth: '100px',
		},
		{
			field: 'email',
			label: 'Email',
			colSpan: 2,
			minWidth: '200px',
		},
		...(customFields.map((field) => ({
			field: field as keyof User,
			label: field,
			sortable: true,
			sortPrefix: 'customFields.',
			colSpan: 2,
			minWidth: '150px',
			render: (user: User) => {
				const customFieldsData = user.customFields as Record<string, string> | undefined
				return customFieldsData?.[field] || ''
			},
		})) || []),
		{
			field: 'active',
			label: 'Status',
			minWidth: '100px',
			render: (user: User) => (
				<div className='flex'>
					<Label className='self-start' color={user.active ? 'green' : 'default'}>
						{user.active ? 'Active' : 'Inactive'}
					</Label>
				</div>
			),
		},
	]

	return (
		<main className='w-full p-6 flex flex-col gap-4'>
			{isPopulationLocked && (
				<div className='rounded-lg border border-amber-200 bg-amber-50 px-4 py-3'>
					<Text variant='small' className='text-amber-900'>
						{populationLock?.reason ??
							'Population editing is locked because this organization has a Data Feed configured.'}
					</Text>
				</div>
			)}
			<div className='mb-2'>
				<Text variant='h3' weight='semibold'>
					Users
				</Text>
				<Text variant='small' className='text-neutral-600'>
					Manage users in your organization
				</Text>
			</div>

			<div className='border border-neutral-200 bg-neutral-0 rounded-lg shadow-xl'>
				<GenericTable<User, UserSearchInput>
					columns={columns}
					idField='id'
					queryOptionsGenerator={queryOptionsGenerator}
					selectable
					showSearch
					showPagination
					onSelectionChange={setSelectedUsers}
					onRowClick={canMutatePopulation ? (user) => navigate(`/users/${user.id}`) : undefined}
					actionButtons={
						canMutatePopulation
							? [
									{
										icon: AddIcon,
										iconPosition: 'left',
										children: 'Add User',
										variant: 'tertiary',
										onClick: () => navigate('/users/create'),
									},
								]
							: []
					}
					emptyState={{
						icon: UsersIcon,
						title: 'No users found',
						description: 'Get started by adding a user',
					}}
				/>
			</div>

			<Dialog
				isOpen={isArchiveModalOpen}
				onClose={() => setIsArchiveModalOpen(false)}
				onConfirm={handleArchiveSelectedUsers}
				title='Archive users'
				message={`You are about to archive ${selectedUsers.length} ${selectedUsers.length === 1 ? 'user' : 'users'}. This action cannot be reversed.`}
				type='archive'
				confirmLabel='Archive'
				requireConfirmText
			/>

			{canMutatePopulation && selectedUsers.length > 0 && (
				<ActionBar
					rightActions={[
						...(selectedUsers.some((user) => user.active)
							? [
									{
										label: 'Set as inactive',
										variant: 'secondary' as const,
										onClick: handleDeactivateSelectedUsers,
									},
								]
							: []),
						...(selectedUsers.some((user) => !user.active)
							? [
									{
										label: 'Set as active',
										variant: 'secondary' as const,
										onClick: handleActivateSelectedUsers,
									},
								]
							: []),
						{
							label: `Archive ${selectedUsers.length > 1 ? `(${selectedUsers.length})` : ''}`,
							variant: 'destructive',
							onClick: () => setIsArchiveModalOpen(true),
						},
					]}
				/>
			)}
		</main>
	)
}
